(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
    return;
  }

  root.WifiCandyOrderAheadLogic = factory();
})(typeof self !== 'undefined' ? self : globalThis, function () {
  const DEFAULT_RETURN_BY_PICKUP = {
    pickup_dublin_airport: 'return_dublin_airport',
    pickup_shannon_airport: 'return_dublin_airport',
    pickup_ireland_delivery: 'return_ireland',
    pickup_uk_delivery: 'return_uk',
    pickup_eu_delivery: 'return_eu',
    pickup_us_delivery: 'return_us'
  };

  const PICKUP_LEAD_TIME_DAYS = {
    pickup_dublin_airport: 0,
    pickup_shannon_airport: 3,
    pickup_ireland_delivery: 3,
    pickup_uk_delivery: 4,
    pickup_eu_delivery: 5,
    pickup_us_delivery: 10
  };
  const RESERVE_PENDING_LABEL = 'Preparing...';
  const RESERVE_PENDING_MESSAGE = 'Preparing your reservation. This usually takes around 8 seconds.';

  function getDefaultReturn(pickupKey, defaultReturnMap = DEFAULT_RETURN_BY_PICKUP) {
    return defaultReturnMap[pickupKey] || 'return_dublin_airport';
  }

  function getPickupLeadTimeDays(pickupKey) {
    return Number(PICKUP_LEAD_TIME_DAYS[pickupKey] || 0);
  }

  function startOfDay(value) {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return null;
    }

    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  function parseDateInput(value) {
    if (value instanceof Date) {
      return startOfDay(value);
    }

    const normalized = String(value || '').trim();
    if (!normalized) {
      return null;
    }

    const isoMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    }

    const dmyMatch = normalized.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (dmyMatch) {
      return new Date(Number(dmyMatch[3]), Number(dmyMatch[2]) - 1, Number(dmyMatch[1]));
    }

    const parsed = new Date(normalized);
    return startOfDay(parsed);
  }

  function hasMinimumRentalDays(startDate, endDate, minimumDays) {
    const start = parseDateInput(startDate);
    const end = parseDateInput(endDate);
    if (!start || !end) {
      return false;
    }

    const minimumEnd = new Date(start);
    minimumEnd.setDate(minimumEnd.getDate() + Math.max(0, Number(minimumDays) || 0));
    return end >= minimumEnd;
  }

  function syncRentalRangePicker(picker, startDate, endDate) {
    if (!picker || typeof picker.setDate !== 'function' || !startDate) {
      return false;
    }

    picker.setDate(endDate ? [startDate, endDate] : [startDate], Boolean(endDate), 'Y-m-d');
    return true;
  }

  function addBusinessDays(value, businessDays) {
    const baseDate = parseDateInput(value);
    if (!baseDate) {
      return null;
    }

    let remaining = Math.max(0, Number(businessDays) || 0);
    const cursor = new Date(baseDate);

    while (remaining > 0) {
      cursor.setDate(cursor.getDate() + 1);
      const day = cursor.getDay();
      if (day !== 0 && day !== 6) {
        remaining -= 1;
      }
    }

    return cursor;
  }

  function getBusinessDaysUntil(value, now = new Date()) {
    const target = parseDateInput(value);
    const baseDate = parseDateInput(now);

    if (!target || !baseDate || target <= baseDate) {
      return 0;
    }

    let total = 0;
    const cursor = new Date(baseDate);

    while (cursor < target) {
      cursor.setDate(cursor.getDate() + 1);
      const day = cursor.getDay();
      if (day !== 0 && day !== 6) {
        total += 1;
      }
    }

    return total;
  }

  function getPickupAvailability({ pickupKey, rentalStartDate, now = new Date() }) {
    const leadDays = getPickupLeadTimeDays(pickupKey);
    const startDate = parseDateInput(rentalStartDate);
    const earliestDate = addBusinessDays(now, leadDays);
    const businessDaysUntilPickup = startDate ? getBusinessDaysUntil(startDate, now) : 0;
    const isAvailable = leadDays === 0 || Boolean(startDate && earliestDate && startDate >= earliestDate);

    return {
      leadDays,
      startDate,
      earliestDate,
      businessDaysUntilPickup,
      isAvailable
    };
  }

  function shouldHandleIzyRentCartAdd({ pendingRedirectUntil, now, requestUrl }) {
    if (!pendingRedirectUntil || now > pendingRedirectUntil) {
      return false;
    }

    return (
      requestUrl.includes('/cart/add.js?izyrent') &&
      !requestUrl.includes('wificandy_supplemental')
    );
  }

  function extractRequestUrl(input) {
    if (typeof input === 'string') {
      return input;
    }

    if (input && typeof input.url === 'string') {
      return input.url;
    }

    if (input && typeof input.href === 'string') {
      return input.href;
    }

    return String(input || '');
  }

  function buildCheckoutSummary({ rentalCents, pickupCents, returnCents, addonCents = 0 }) {
    return {
      rentalCents,
      pickupCents,
      returnCents,
      addonCents,
      totalCents: rentalCents + pickupCents + returnCents + addonCents
    };
  }

  function hasSelectedRentalDates(rentalLabel) {
    return Boolean(String(rentalLabel || '').trim());
  }

  function hasSelectedLogistics({ pickupKey = '', returnKey = '' }) {
    return Boolean(String(pickupKey).trim()) && Boolean(String(returnKey).trim());
  }

  function isReservationReady({
    rentalLabel,
    rentalCents = 0,
    pickupKey = '',
    returnKey = ''
  }) {
    return (
      Number(rentalCents) > 0 &&
      hasSelectedRentalDates(rentalLabel) &&
      hasSelectedLogistics({ pickupKey, returnKey })
    );
  }

  function getReserveButtonLabel({
    totalLabel,
    isProcessing,
    isReady,
    rentalLabel = '',
    pickupKey = '',
    returnKey = '',
    isResolving = false
  }) {
    if (isProcessing) {
      return RESERVE_PENDING_LABEL;
    }

    if (isResolving) {
      return 'Confirming total...';
    }

    if (!hasSelectedRentalDates(rentalLabel)) {
      return 'Choose your dates';
    }

    if (!hasSelectedLogistics({ pickupKey, returnKey })) {
      return 'Add pickup / delivery & return';
    }

    if (!isReady) {
      return 'Complete your reservation';
    }

    return `Reserve ${totalLabel}`;
  }

  function getReserveStatusMessage({
    rentalLabel,
    pickupKey = '',
    returnKey = '',
    isResolving = false,
    isProcessing,
    errorMessage = ''
  }) {
    if (isProcessing) {
      return RESERVE_PENDING_MESSAGE;
    }

    if (errorMessage) {
      return errorMessage;
    }

    if (isResolving) {
      return 'We’re confirming your rental total. Please wait a moment.';
    }

    const hasDates = hasSelectedRentalDates(rentalLabel);
    const hasPickup = Boolean(String(pickupKey).trim());
    const hasReturn = Boolean(String(returnKey).trim());

    if (!hasDates && !hasPickup && !hasReturn) {
      return 'Choose your travel dates, pickup / delivery, and return to continue.';
    }

    if (!hasDates) {
      return 'Choose your travel dates to continue.';
    }

    if (!hasPickup && !hasReturn) {
      return 'Choose your pickup / delivery and return options to continue.';
    }

    if (!hasPickup) {
      return 'Choose your pickup / delivery option to continue.';
    }

    if (!hasReturn) {
      return 'Choose your return option to continue.';
    }

    return '';
  }

  function getSummaryMetaText({
    rentalLabel,
    rentalCents = 0,
    pickupKey = '',
    returnKey = '',
    readyText = ''
  }) {
    if (isReservationReady({ rentalLabel, rentalCents, pickupKey, returnKey })) {
      return readyText;
    }

    const hasDates = hasSelectedRentalDates(rentalLabel);
    const hasPickup = Boolean(String(pickupKey).trim());
    const hasReturn = Boolean(String(returnKey).trim());

    if (!hasDates && !hasPickup && !hasReturn) {
      return 'Choose your dates, pickup / delivery, and return to see the total.';
    }

    if (!hasDates) {
      return 'Choose your travel dates to see the total.';
    }

    if (!hasPickup && !hasReturn) {
      return 'Choose your pickup / delivery and return options to see the total.';
    }

    if (!hasPickup) {
      return 'Choose your pickup / delivery option to see the total.';
    }

    if (!hasReturn) {
      return 'Choose your return option to see the total.';
    }

    return 'We’re confirming your rental total. Please wait a moment.';
  }

  function shouldShowPlaceholderTotal({ rentalLabel, rentalCents = 0, pickupKey = '', returnKey = '' }) {
    return !isReservationReady({ rentalLabel, rentalCents, pickupKey, returnKey });
  }

  function getLogisticsState({ pickupKey = '', returnKey = '' }) {
    const hasPickup = Boolean(String(pickupKey).trim());
    const hasReturn = Boolean(String(returnKey).trim());

    if (hasPickup && hasReturn) {
      return 'complete';
    }

    if (hasPickup || hasReturn) {
      return 'partial';
    }

    return 'incomplete';
  }

  function getLogisticsProgressText({ pickupKey = '', returnKey = '' }) {
    const hasPickup = Boolean(String(pickupKey).trim());
    const hasReturn = Boolean(String(returnKey).trim());

    if (hasPickup && hasReturn) {
      return 'Pickup / delivery and return confirmed.';
    }

    if (hasPickup) {
      return 'Pickup / delivery saved. Choose return next.';
    }

    if (hasReturn) {
      return 'Choose pickup / delivery to continue.';
    }

    return 'Pickup / delivery and return needed';
  }

  function getMissingLogisticsStep({ pickupKey = '', returnKey = '' }) {
    if (!String(pickupKey).trim()) {
      return 'pickup';
    }

    if (!String(returnKey).trim()) {
      return 'return';
    }

    return null;
  }

  function parseMoneyTextToCents(text) {
    const cleaned = String(text || '')
      .replace(/[^0-9,.-]/g, '')
      .trim();

    if (!cleaned) {
      return 0;
    }

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    let decimalSeparator = '';

    if (lastComma !== -1 && lastDot !== -1) {
      decimalSeparator = lastComma > lastDot ? ',' : '.';
    } else if (lastComma !== -1) {
      decimalSeparator = ',';
    } else if (lastDot !== -1) {
      decimalSeparator = '.';
    }

    if (!decimalSeparator) {
      return Math.round(Number(cleaned.replace(/[^\d-]/g, '')) * 100) || 0;
    }

    const separatorIndex = cleaned.lastIndexOf(decimalSeparator);
    const integerPart = cleaned.slice(0, separatorIndex).replace(/[.,]/g, '');
    const decimalPart = cleaned
      .slice(separatorIndex + 1)
      .replace(/[^\d]/g, '')
      .padEnd(2, '0')
      .slice(0, 2);

    const normalized = `${integerPart || '0'}.${decimalPart || '00'}`;
    return Math.round(Number(normalized) * 100) || 0;
  }

  function extractIzyRecapState(html) {
    const source = String(html || '');
    const rentalLabelMatch = source.match(/class=["']nbdays["'][^>]*>([^<]+)/i);
    const moneyMatch = source.match(/class=["']money["'][^>]*>([^<]+)/i);

    return {
      rentalCents: parseMoneyTextToCents(moneyMatch ? moneyMatch[1] : ''),
      rentalLabel: rentalLabelMatch ? rentalLabelMatch[1].trim() : ''
    };
  }

  function buildSupplementalItems({
    bundleKey,
    pickupVariantId,
    returnVariantId,
    powercandyVariantId = null,
    parentProductUrl
  }) {
    const items = [
      {
        id: pickupVariantId,
        quantity: 1,
        properties: {
          __wificandyBundleKey: bundleKey,
          __wificandyBundleRole: 'pickup',
          __wificandyParentUrl: parentProductUrl
        }
      },
      {
        id: returnVariantId,
        quantity: 1,
        properties: {
          __wificandyBundleKey: bundleKey,
          __wificandyBundleRole: 'return',
          __wificandyParentUrl: parentProductUrl
        }
      }
    ];

    if (powercandyVariantId) {
      items.push({
        id: powercandyVariantId,
        quantity: 1,
        properties: {
          __wificandyBundleKey: bundleKey,
          __wificandyBundleRole: 'addon',
          __wificandyParentUrl: parentProductUrl
        }
      });
    }

    return items;
  }

  function buildCatalogProductAddonConfig(product) {
    const variants = Array.isArray(product && product.variants) ? product.variants : [];
    const variant = variants.find((item) => item && item.available) || variants[0] || null;

    return {
      productId: Number(product && product.id) || null,
      variantId: variant ? Number(variant.id) || null : null,
      priceCents: variant ? Number(variant.price) || 0 : 0
    };
  }

  return {
    buildCatalogProductAddonConfig,
    buildCheckoutSummary,
    buildSupplementalItems,
    addBusinessDays,
    extractRequestUrl,
    extractIzyRecapState,
    getBusinessDaysUntil,
    getReserveButtonLabel,
    getReserveStatusMessage,
    getDefaultReturn,
    getPickupAvailability,
    getPickupLeadTimeDays,
    getLogisticsProgressText,
    getLogisticsState,
    getMissingLogisticsStep,
    getSummaryMetaText,
    hasMinimumRentalDays,
    hasSelectedRentalDates,
    hasSelectedLogistics,
    isReservationReady,
    parseDateInput,
    parseMoneyTextToCents,
    shouldShowPlaceholderTotal,
    syncRentalRangePicker,
    shouldHandleIzyRentCartAdd
  };
});
